/**
 * Hitsound buffers and voices (W1-14, A§8, R§5.4, S§5).
 *
 * `SoundStore` is the engine's HitsoundBank: skin `click`/`drag`/`flick` installed from the active skin
 * (`setSkinSounds`, Hold heads use click, no hold sound exists) or from an app-supplied bank (`setHitsounds`),
 * procedural fallbacks for every empty slot (synth.ts), and custom per-note hitsound assets. An asset that is not
 * decoded (yet) sounds as click so the note is still audible; the checker reports the missing file.
 *
 * `VoicePool` owns the AudioBufferSourceNodes: one voice per (buffer, start time); emissions that start within 1 ms
 * on the same buffer share a voice whose gain is the number of merged notes (dense chords sum like separate
 * sounds but cost one node), and at most 48 voices sound at once: a new voice steals the oldest sounding one,
 * which fades out over 3 ms.
 */

import type { AssetId } from '../core/model/types';
import type { HitsoundBank } from './types';
import { synthBuffer, type BufferFactory } from './synth';

export type SkinSoundName = 'click' | 'drag' | 'flick';

/** What an emission or a judged hit asks to play. */
export type SoundRef =
  { readonly kind: 'skin'; readonly name: SkinSoundName } | { readonly kind: 'asset'; readonly asset: AssetId };

/** Stable identity of a SoundRef (part of scheduler keys). */
export function soundKey(sound: SoundRef): string {
  return sound.kind === 'skin' ? sound.name : `asset:${sound.asset}`;
}

export class SoundStore implements HitsoundBank {
  private readonly skin: Record<SkinSoundName, AudioBuffer | null> = { click: null, drag: null, flick: null };
  private procedural: Record<SkinSoundName, AudioBuffer> | null = null;
  private factory: BufferFactory | null = null;
  private readonly customs = new Map<AssetId, AudioBuffer>();
  private external: HitsoundBank | null = null;

  /** Binds the context that creates the procedural fallbacks (rebuilt for a new context). */
  bind(factory: BufferFactory): void {
    if (factory === this.factory) return;
    this.factory = factory;
    this.procedural = null;
  }

  private fallback(name: SkinSoundName): AudioBuffer {
    if (this.procedural === null) {
      const f = this.factory;
      if (f === null) throw new Error('SoundStore: no audio context bound for the procedural sounds');
      this.procedural = {
        click: synthBuffer(f, 'click'),
        drag: synthBuffer(f, 'drag'),
        flick: synthBuffer(f, 'flick'),
      };
    }
    return this.procedural[name];
  }

  get click(): AudioBuffer {
    return this.skin.click ?? this.fallback('click');
  }

  get drag(): AudioBuffer {
    return this.skin.drag ?? this.fallback('drag');
  }

  get flick(): AudioBuffer {
    return this.skin.flick ?? this.fallback('flick');
  }

  skinSound(name: SkinSoundName): AudioBuffer {
    return this.skin[name] ?? this.fallback(name);
  }

  /** Whether a slot holds a skin (non-procedural) sound. */
  hasSkinSound(name: SkinSoundName): boolean {
    return this.skin[name] !== null;
  }

  /** Installs a skin sound; null restores the procedural fallback. */
  setSkin(name: SkinSoundName, buffer: AudioBuffer | null): void {
    this.skin[name] = buffer;
  }

  /** Installs an app-supplied bank: its skin sounds fill the slots and its custom sounds back up ours. */
  setExternal(bank: HitsoundBank): void {
    this.external = bank;
    this.skin.click = bank.click;
    this.skin.drag = bank.drag;
    this.skin.flick = bank.flick;
  }

  setCustom(asset: AssetId, buffer: AudioBuffer): void {
    this.customs.set(asset, buffer);
  }

  custom(asset: AssetId): AudioBuffer | undefined {
    return this.customs.get(asset) ?? this.external?.custom(asset);
  }

  /** The buffer a sound plays (asset not decoded -> click). */
  resolve(sound: SoundRef): AudioBuffer {
    if (sound.kind === 'skin') return this.skinSound(sound.name);
    return this.custom(sound.asset) ?? this.skinSound('click');
  }
}

/** Polyphony cap (A§8). */
export const MAX_VOICES = 48;
/** Emissions on the same buffer starting this close together share one voice. */
export const MERGE_TOLERANCE_SEC = 0.001;
/** Fade applied to a stolen voice. */
export const STEAL_FADE_SEC = 0.003;

export interface Voice {
  readonly buffer: AudioBuffer;
  readonly bus: AudioNode;
  /** Scheduled start (context time). */
  readonly when: number;
  /** When it stops sounding (start + duration, or the steal time). */
  end: number;
  /** Number of merged items; the voice gain. */
  count: number;
  cancelled: boolean;
  readonly src: AudioBufferSourceNode;
  readonly gain: GainNode;
}

/** The context surface the pool needs. */
export interface VoiceContext {
  createBufferSource(): AudioBufferSourceNode;
  createGain(): GainNode;
}

export class VoicePool {
  readonly cap: number;
  private voices: Voice[] = [];

  constructor(cap = MAX_VOICES) {
    this.cap = cap;
  }

  /** Voices not yet pruned (scheduled, sounding or recently ended). */
  get size(): number {
    return this.voices.length;
  }

  /** Voices sounding at context time `t`. */
  soundingAt(t: number): number {
    let n = 0;
    for (const v of this.voices) if (!v.cancelled && v.when <= t && t < v.end) n++;
    return n;
  }

  /**
   * Schedules `buffer` at `when` on `bus`, or merges into a pending voice of the same buffer and bus starting within
   * MERGE_TOLERANCE_SEC (pass merge = false for immediate one-shots). `now` is the context's currentTime.
   */
  acquire(ctx: VoiceContext, buffer: AudioBuffer, when: number, bus: AudioNode, now: number, merge = true): Voice {
    if (merge) {
      for (const v of this.voices) {
        if (
          !v.cancelled &&
          v.buffer === buffer &&
          v.bus === bus &&
          v.when > now &&
          Math.abs(v.when - when) <= MERGE_TOLERANCE_SEC
        ) {
          v.count++;
          v.gain.gain.value = v.count;
          return v;
        }
      }
    }
    this.prune(now);
    this.makeRoom(when, now);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = 1;
    src.connect(gain);
    gain.connect(bus);
    src.onended = () => {
      gain.disconnect();
    };
    src.start(Math.max(0, when));
    const voice: Voice = { buffer, bus, when, end: when + buffer.duration, count: 1, cancelled: false, src, gain };
    // Keep the list ordered by start time (oldest first) for stealing.
    let i = this.voices.length;
    while (i > 0 && (this.voices[i - 1]?.when ?? -Infinity) > when) i--;
    this.voices.splice(i, 0, voice);
    return voice;
  }

  /** Steals the oldest voice sounding at `when` while the cap would be exceeded. */
  private makeRoom(when: number, now: number): void {
    let sounding = this.soundingAt(when);
    if (sounding < this.cap) return;
    for (const v of this.voices) {
      if (sounding < this.cap) break;
      if (v.cancelled || !(v.when <= when && when < v.end)) continue;
      this.stopAt(v, when, now);
      sounding--;
    }
  }

  /** Fades `v` out and stops it at `t` (>= now). */
  private stopAt(v: Voice, t: number, now: number): void {
    const at = Math.max(t, now);
    const p = v.gain.gain;
    const fadeStart = Math.max(now, at - STEAL_FADE_SEC, v.when);
    p.cancelScheduledValues(fadeStart);
    p.setValueAtTime(v.count, fadeStart);
    p.linearRampToValueAtTime(0, at);
    v.src.stop(at);
    v.end = Math.min(v.end, at);
  }

  /** One merged item of `v` is gone; a pending voice with no item left never plays. */
  release(v: Voice, now: number): void {
    if (v.cancelled) return;
    v.count--;
    if (v.count > 0) {
      if (v.when > now) v.gain.gain.value = v.count;
      return;
    }
    if (v.when > now) this.cancel(v);
  }

  private cancel(v: Voice): void {
    v.cancelled = true;
    v.end = v.when;
    try {
      v.src.stop();
    } catch {
      // Already stopped.
    }
    v.gain.disconnect();
    const i = this.voices.indexOf(v);
    if (i >= 0) this.voices.splice(i, 1);
  }

  /** Cancels every pending voice and fades out the sounding ones (dispose). */
  stopAll(now: number): void {
    for (const v of [...this.voices]) {
      if (v.cancelled) continue;
      if (v.when > now) this.cancel(v);
      else if (v.end > now) this.stopAt(v, now + STEAL_FADE_SEC, now);
    }
  }

  /** Drops voices that finished before `now` (their nodes disconnect on `ended`). */
  prune(now: number): void {
    this.voices = this.voices.filter((v) => !v.cancelled && v.end > now - 0.05);
  }
}
